import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchArgs, FetchBaseQueryError, BaseQueryFn } from '@reduxjs/toolkit/query';

import {
  clearShopSession,
  setApiKey,
  switchShopComplete,
} from '@/features/shops/model/shops.slice';
import { setUser } from '@/features/users/current-user.slice';
import type { SwitchShopResponse } from '@/types/shop';

import { setAuthenticated } from './auth/model';
import { AUTH_ROUTES } from './auth/routes';
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  setAuthTokens,
} from './auth/token-storage';

const getApiBaseUrl = (): string => {
  const defaultUrl = import.meta.env.VITE_IOKA_API_URL;
  return defaultUrl;
};

const baseUrl: string = getApiBaseUrl();

interface ApiState {
  shops?: {
    apiKey?: string | null;
    selectedShopId?: string | null;
    role?: SwitchShopResponse['role'] | null;
  };
}

interface AuthTokens {
  access_token: string;
  refresh_token?: string;
}

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = getAccessToken();
    const state = getState() as ApiState;
    const apiKey = state.shops?.apiKey ?? null;

    if (token && endpoint !== 'createOrdersPayout' && endpoint !== 'confirmOrdersPayout') {
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (apiKey) {
      headers.set('API-KEY', apiKey);
      headers.set('Authorization', `Bearer ${apiKey}`);
    } else if (token) {
      headers.set('API-KEY', token);
    }

    return headers;
  },
});

const authBaseQuery = fetchBaseQuery({ baseUrl });

let refreshSessionPromise: Promise<AuthTokens> | null = null;

const getSwitchUrl = (shopId: string | null) =>
  shopId && shopId !== 'all' ? `/v2/shops/switch?shop_id=${shopId}` : '/v2/shops/switch';

const refreshActiveSession = async (
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2],
) => {
  if (refreshSessionPromise) {
    return refreshSessionPromise;
  }

  const currentRefreshToken = getRefreshToken();

  if (!currentRefreshToken) {
    throw new Error('Refresh token is missing');
  }

  refreshSessionPromise = (async () => {
    const refreshResult = await authBaseQuery(
      {
        url: AUTH_ROUTES.REFRESH_TOKEN,
        method: 'POST',
        body: {
          grant_type: 'refresh_token',
          refresh_token: currentRefreshToken,
        },
      },
      api,
      extraOptions,
    );

    if (refreshResult.error || !refreshResult.data) {
      throw refreshResult.error ?? new Error('Refresh token request failed');
    }

    const refreshedTokens = refreshResult.data as AuthTokens;
    const nextRefreshToken = refreshedTokens.refresh_token ?? currentRefreshToken;

    if (!refreshedTokens.access_token) {
      throw new Error('Access token is missing');
    }

    setAuthTokens(refreshedTokens.access_token, nextRefreshToken);

    const shopState = (api.getState() as ApiState).shops;
    const selectedShopId = shopState?.selectedShopId ?? null;

    if (!selectedShopId) {
      if (shopState?.apiKey) {
        api.dispatch(setApiKey(refreshedTokens.access_token));
      }

      return {
        access_token: refreshedTokens.access_token,
        refresh_token: nextRefreshToken,
      };
    }

    const switchResult = await authBaseQuery(
      {
        url: getSwitchUrl(selectedShopId),
        method: 'GET',
        headers: {
          Authorization: `Bearer ${refreshedTokens.access_token}`,
          'API-KEY': refreshedTokens.access_token,
        },
      },
      api,
      extraOptions,
    );

    if (switchResult.error || !switchResult.data) {
      throw switchResult.error ?? new Error('Switch shop request failed');
    }

    const switchedSession = switchResult.data as SwitchShopResponse;
    setAuthTokens(switchedSession.access_token, switchedSession.refresh_token);
    localStorage.setItem('selectedShopId', selectedShopId);
    api.dispatch(
      switchShopComplete({
        shopId: selectedShopId,
        apiKey: switchedSession.access_token,
        role: switchedSession.role ?? shopState?.role ?? null,
        refreshToken: switchedSession.refresh_token,
      }),
    );

    return {
      access_token: switchedSession.access_token,
      refresh_token: switchedSession.refresh_token,
    };
  })().finally(() => {
    refreshSessionPromise = null;
  });

  return refreshSessionPromise;
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    try {
      await refreshActiveSession(api, extraOptions);
      api.dispatch(setAuthenticated(true));

      result = await baseQuery(args, api, extraOptions);
    } catch {
      clearAuthTokens();
      api.dispatch(clearShopSession());
      api.dispatch(setUser(null));
      api.dispatch(setAuthenticated(false));
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Order',
    'Shop',
    'User',
    'Store',
    'ApiKey',
    'Acquirer',
    'Employee',
    'Share',
    'Turnover',
    'OrganizationAnswers',
    'Balance',
    'BalanceDetails',
    'FAQ',
    'ShopConfig',
    'Payout',
    'Report',
    'BlackList',
    'OrganizationRiskLevel',
    'OrganizationDetails',
    'OrganizationComments',
    'Promo',
    'PromoDetails',
    'PaymentsWithCommands',
    'PromoRestrictions',
    'RestrictionDetails',
    'ShopsAcquirers',
    'faqCategories',
    'Reestrs',
    'PayoutReestrs',
    'OrganizationStatusHistory',
    'Notifications',
    'NotificationsUnreadCount',
    'AllNotifications',
    'Webhooks',
  ],
  endpoints: () => ({}),
});
